/**
 * Template Build Page
 *
 * Page component for AI-powered template creation.
 * Features:
 * - Chat sidebar on the right for AI interactions
 * - Work area on the left for displaying template preview and sections
 * - Supports document upload or text paste to start
 */

import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAdminBuildSaveMutation } from '@features/admin-v2/store/hooks';
import { EntitlementGate } from '@features/entitlements';
import { templatesApi } from '@features/templates/store/api';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useFeature } from '@store/features/hooks';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { TemplateBuilderChat } from '../components/TemplateBuilderChat';
import { TemplateWorkArea } from '../components/TemplateWorkArea';
import { useAdminTemplateBuildStatus } from '../hooks/useAdminTemplateBuildStatus';
import { useHydrateSavedDraftSignature } from '../hooks/useHydrateSavedDraftSignature';
import { useTemplateBuildActions } from '../hooks/useTemplateBuildActions';
import { useTemplateBuildStatus } from '../hooks/useTemplateBuildStatus';
import { useBuildFromTemplateMutation, useSaveTemplateBuildMutation } from '../store/api';
import {
  handOffToForkedTemplate,
  initializeTemplateBuild,
  markDraftSaved,
  resetBuild,
  setCreatedTemplateId,
  setTaskId,
} from '../store/chatSlice';
import { templatesAiEditFeature } from '../store/feature';
import {
  selectAssistantStatus,
  selectBuildStatus,
  selectDraftTemplate,
  selectIsDraftSaved,
  selectMessages,
  selectSourceTemplateId,
  selectTaskId,
} from '../store/selectors';
import { shouldHandOffToFork } from '../types';

// Chat sidebar width
const CHAT_WIDTH = 420;

/**
 * Shown in the chat after a save forks a template the provider didn't own.
 * The task changes, so the thread resets — say why rather than silently
 * emptying it (GDN-3099).
 */
const FORK_HANDOFF_NOTICE =
  "Public templates can't be edited directly, so your changes were saved into your own copy. " +
  "You're editing that copy now, and everything you changed so far is already in it. " +
  'This chat starts over because the copy is a new template — nothing was lost. ' +
  'What would you like to change next?';

/**
 * Page component for the Template Build feature
 */
export const TemplateBuildPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams<{ taskId?: string }>();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { templateBuild } = useEventTracking();

  // Extract taskId from URL params
  const urlTaskId = params.taskId || null;

  // Check if in admin mode
  const isAdminMode = searchParams.get('admin') === 'true';
  const targetUserId = searchParams.get('targetUserId') || null;

  // Ensure feature is registered
  const isFeatureReady = useFeature(templatesAiEditFeature);

  // Redux state
  const messages = useAppSelector(selectMessages);
  const assistantStatus = useAppSelector(selectAssistantStatus);
  const storeTaskId = useAppSelector(selectTaskId);
  const buildStatus = useAppSelector(selectBuildStatus);
  const draftTemplate = useAppSelector(selectDraftTemplate);
  const isDraftSaved = useAppSelector(selectIsDraftSaved);
  const sourceTemplateId = useAppSelector(selectSourceTemplateId);

  // Effective task ID - URL param takes precedence
  const effectiveTaskId = urlTaskId || storeTaskId;

  // Hooks - use admin hooks if in admin mode
  const regularStatus = useTemplateBuildStatus(isAdminMode ? null : effectiveTaskId);
  const adminStatus = useAdminTemplateBuildStatus(isAdminMode ? effectiveTaskId : null);
  const { isReadyForChat } = isAdminMode ? adminStatus : regularStatus;
  // targetUserId is only available from admin status hook
  const statusTargetUserId = isAdminMode ? adminStatus.targetUserId : null;

  const chatActions = useTemplateBuildActions(effectiveTaskId, isFeatureReady);

  // Resuming a task that was saved before this page load: recover whether the
  // draft on screen is the published one. Skipped in admin mode — the provider
  // templates endpoint answers for the caller, not the target user.
  const { isReconciling } = useHydrateSavedDraftSignature(!isAdminMode);

  // Save mutations - use admin mutation if in admin mode
  const [saveTemplateBuild, { isLoading: isSavingRegular }] = useSaveTemplateBuildMutation();
  const [adminSaveTemplateBuild, { isLoading: isSavingAdmin }] = useAdminBuildSaveMutation();
  const [buildFromTemplate, { isLoading: isHandingOff }] = useBuildFromTemplateMutation();
  const isSaving = (isAdminMode ? isSavingAdmin : isSavingRegular) || isHandingOff;

  // Task ids this component seeded itself during a fork hand-off. Re-running
  // the init effect for one would wipe the explanation and the copy's context.
  const handedOffTaskIdRef = useRef<string | null>(null);

  // Handle initialization
  useEffect(() => {
    if (!isFeatureReady) return;

    // Already populated by the fork hand-off below — don't reset over it.
    if (urlTaskId && urlTaskId === handedOffTaskIdRef.current) return;

    if (urlTaskId) {
      // Initialize with URL task ID
      dispatch(initializeTemplateBuild({ taskId: urlTaskId, threadId: null }));
      dispatch(setTaskId(urlTaskId));
    } else {
      // No taskId in URL - always reset to upload screen state
      // This handles both fresh starts and navigation back from a build
      dispatch(initializeTemplateBuild({ taskId: null, threadId: null }));
    }
  }, [isFeatureReady, urlTaskId, dispatch]);

  // Cleanup on unmount - reset state and stop polling
  useEffect(() => {
    return () => {
      dispatch(resetBuild());
    };
  }, [dispatch]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    if (isAdminMode && targetUserId) {
      // Navigate back to admin provider page (we don't have template ID here, so go to provider)
      navigate(`/admin-v2/provider/${encodeURIComponent(targetUserId)}`);
    } else if (effectiveTaskId && buildStatus !== 'idle') {
      // User has an active build session - go back to upload screen
      dispatch(resetBuild());
      navigate('/templates/build');
    } else {
      navigate('/templates');
    }
  }, [navigate, isAdminMode, targetUserId, effectiveTaskId, buildStatus, dispatch]);

  // Handle save template
  const handleSave = useCallback(async () => {
    if (!effectiveTaskId) return;

    try {
      let result;
      if (isAdminMode) {
        // Use admin save mutation
        result = await adminSaveTemplateBuild({ taskId: effectiveTaskId }).unwrap();
      } else {
        // Use regular save mutation
        result = await saveTemplateBuild({ taskId: effectiveTaskId }).unwrap();
      }

      // Track save event
      templateBuild.completed(effectiveTaskId, {
        template_id: result.template_id,
        section_count: draftTemplate?.sections.length,
      });

      // Record the save without ending the session. Flipping the status to
      // 'completed' here used to unmount the preview and the chat, so the
      // provider lost sight of the very change they had just saved — the
      // "my edits disappeared" report in CUS-1169. The confirmation now
      // renders above the still-visible template (GDN-3099).
      dispatch(setCreatedTemplateId(result.template_id));
      dispatch(markDraftSaved());

      // Invalidate templates cache so the templates page shows the new/updated template
      dispatch(
        templatesApi.util.invalidateTags([
          { type: 'TemplateList', id: 'LIST' },
          { type: 'Template', id: result.template_id },
        ]),
      );

      const targetUserDisplay =
        isAdminMode && (statusTargetUserId || targetUserId)
          ? ` for user ${statusTargetUserId || targetUserId}`
          : '';

      enqueueSnackbar(`Template saved successfully${targetUserDisplay}!`, { variant: 'success' });

      if (isAdminMode && targetUserId) {
        // Navigate back to admin template detail page
        navigate(
          `/admin-v2/provider/${encodeURIComponent(targetUserId)}/templates/${result.template_id}`,
        );
        return;
      }

      // The save landed somewhere other than where this session started, so the
      // server forked — the source wasn't ours to edit. It forks from that same
      // source on every save, so carrying on here would mint another copy per
      // save. Re-seed onto the copy, which we now own and which updates in
      // place (GDN-3099).
      if (
        shouldHandOffToFork({
          isAdminMode,
          sourceTemplateId,
          savedTemplateId: result.template_id,
        })
      ) {
        try {
          const rebuilt = await buildFromTemplate({
            templateBuildFromTemplateRequest: { template_id: result.template_id },
          }).unwrap();

          handedOffTaskIdRef.current = rebuilt.task_id;
          dispatch(
            handOffToForkedTemplate({
              taskId: rebuilt.task_id,
              templateId: result.template_id,
              notice: FORK_HANDOFF_NOTICE,
            }),
          );
          navigate(`/templates/build/${rebuilt.task_id}`, { replace: true });
        } catch (handOffError) {
          // The copy exists and holds the change either way — only the ability
          // to keep editing in place is lost. Say so instead of failing the
          // save the provider already completed.
          console.error('Failed to continue on the saved copy:', handOffError);
          enqueueSnackbar(
            'Saved to your own copy. Open it from your templates to keep editing.',
            { variant: 'warning' },
          );
        }
      }
      // Otherwise the save updated this session's template in place, and the
      // provider stays where they are with the saved banner above the preview.
    } catch (error: any) {
      console.error('Failed to save template:', error);
      enqueueSnackbar(error?.data?.message || 'Failed to save template. Please try again.', {
        variant: 'error',
      });
    }
  }, [
    effectiveTaskId,
    isAdminMode,
    targetUserId,
    statusTargetUserId,
    saveTemplateBuild,
    adminSaveTemplateBuild,
    buildFromTemplate,
    sourceTemplateId,
    templateBuild,
    draftTemplate,
    dispatch,
    enqueueSnackbar,
    navigate,
  ]);

  // Handle send message
  const handleSendMessage = useCallback(
    (message: string) => {
      chatActions.sendMessage(message);
    },
    [chatActions],
  );

  // Handle select options
  const handleSelectOptions = useCallback(
    (selectedIds: string[]) => {
      chatActions.selectOptions(selectedIds);
    },
    [chatActions],
  );

  // Handle confirm
  const handleConfirm = useCallback(
    (confirmed: boolean) => {
      chatActions.submitConfirmation(confirmed);
    },
    [chatActions],
  );

  // Show loading if feature not ready
  if (!isFeatureReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography color="text.secondary">Loading...</Typography>
      </Box>
    );
  }

  // A draft on screen means there is still something to edit, whatever the
  // task status says. 'completed' means "the server holds a published copy",
  // not "stop working" — the save-triggered status refetch reports it, and
  // treating it as terminal is what used to close the chat mid-session.
  const hasEditableDraft = Boolean(draftTemplate?.sections.length);
  const isSessionInteractive = Boolean(effectiveTaskId) && (isReadyForChat || hasEditableDraft);

  // Chat is always visible while the session is live (not dismissable here)
  const showChat = isSessionInteractive;

  // Main content JSX
  const mainContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        bgcolor: theme.palette.background.default,
      }}
    >
      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {/* Admin Mode Banner */}
        {isAdminMode && (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            <AlertTitle>Admin Mode</AlertTitle>
            <Typography variant="body2">
              Editing template on behalf of user:{' '}
              <strong>{statusTargetUserId || targetUserId || 'Unknown'}</strong>
            </Typography>
          </Alert>
        )}

        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isAdminMode ? 'Edit Template (Admin)' : 'Create Template'}
            </Typography>
          </Box>

          {/* Save button — stays put after a save so the provider can keep
              editing, and goes inert once the draft matches what was published.
              A publish writes a revision unconditionally, so an enabled button
              over an unchanged draft is an invitation to inflate the version
              history (CUS-1169). */}
          {isSessionInteractive && draftTemplate && (
            <Tooltip title={isDraftSaved ? 'No changes since your last save' : ''}>
              {/* span: a disabled MUI Button swallows the tooltip's events */}
              <span>
                <Button
                  variant="contained"
                  startIcon={
                    isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                  }
                  onClick={handleSave}
                  disabled={
                    isSaving || !draftTemplate.sections.length || isDraftSaved || isReconciling
                  }
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {isSaving ? 'Saving...' : isDraftSaved ? 'Saved' : 'Save Template'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {/* Work Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          <TemplateWorkArea />
        </Box>
      </Box>

      {/* Chat Sidebar */}
      {showChat && (
        <Box
          sx={{
            width: CHAT_WIDTH,
            flexShrink: 0,
            height: '100%',
            bgcolor: theme.palette.background.paper,
            borderLeft: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TemplateBuilderChat
            taskId={effectiveTaskId}
            messages={messages}
            assistantStatus={assistantStatus}
            onSendMessage={handleSendMessage}
            onSelectOptions={handleSelectOptions}
            onConfirm={handleConfirm}
          />
        </Box>
      )}
    </Box>
  );

  // For admin mode, render without gate
  if (isAdminMode) {
    return mainContent;
  }

  // For regular users, wrap with EntitlementGate
  return (
    <EntitlementGate
      capabilityName="template_creation"
      source="template_build_page"
      secondaryAction={{
        label: 'Go Back',
        onClick: handleBack,
      }}
    >
      {mainContent}
    </EntitlementGate>
  );
};

export default TemplateBuildPage;
